/**
 * Hook domain: Quản lý thông báo (Notifications)
 * 
 * Layer: Feature (Domain Logic)
 * Chức năng: Xử lý toàn bộ logic nghiệp vụ liên quan đến thông báo:
 * - Fetch danh sách thông báo từ API
 * - Format thời gian hiển thị
 * - Filter thông báo theo tab (tất cả, chưa đọc, hôm nay)
 * - Đánh dấu đã đọc / đánh dấu tất cả đã đọc
 * - Xử lý click thông báo (navigation logic)
 * - Parse và xác định loại thông báo (sinh nhật, yêu cầu cập nhật, HR update, ...)
 * - Quản lý avatar và cache
 * - Tích hợp socket để nhận thông báo real-time
 * - Polling khi socket không kết nối
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { notificationService, Notification } from '@/services/notification.service';
import { useNotificationSocket } from '@/hooks/useNotificationSocket';
import { buildFullUrl, fetchImageAsDataUri } from '@/contexts/AvatarContext';
import { usersService } from '@/services/users.service';
import { useAuth } from '@/contexts/AuthContext';
import { formatDateDisplay } from '@/utils/dateHelpers';
import { handleApiError } from '@/utils/errorHandler';

export type NotificationTab = 'all' | 'unread' | 'today';

export interface UseNotificationsResult {
  // Data
  notifications: Notification[];
  loading: boolean;
  error: string | null;
  isInitialLoad: boolean;

  // Tabs
  activeTab: NotificationTab;
  setActiveTab: (tab: NotificationTab) => void;
  filteredNotifications: Notification[];

  // Counts
  unreadCount: number;
  todayUnreadCount: number;

  // Actions
  handleNotificationClick: (item: Notification) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  handleRefresh: () => Promise<void>;
  deleteNotifications: (ids: string[]) => Promise<void>;

  // Avatar helpers
  fetchAvatarAsDataUri: (imageUrl: string) => Promise<string | null>;
  avatarCacheRef: React.MutableRefObject<Map<string, string>>;

  // Notification type helpers
  isBirthdayNotification: (item: Notification) => boolean;
  isUpdateRequestNotification: (item: Notification) => boolean;
  isHRUpdateNotification: (item: Notification) => boolean;
  isNewUpdateRequestNotification: (item: Notification) => boolean;
  getBirthdayPersonName: (item: Notification) => string | null;
  getBirthdayPersonId: (item: Notification) => number | null;
  getBirthdayPersonAvatar: (item: Notification) => string | null;
  getApproverInfo: (item: Notification) => { id: number; fullName: string; avatar?: string } | null;
  getRejectorInfo: (item: Notification) => { id: number; fullName: string; avatar?: string } | null;
  getHRUpdaterInfo: (item: Notification) => { id: number; fullName: string; avatar?: string } | null;
  getRequesterInfo: (item: Notification) => { id: number; fullName: string; avatar?: string } | null;

  // Socket
  isConnected: boolean;
}

export const useNotifications = (
  onMarkAsRead?: () => void,
  refreshTrigger?: number
): UseNotificationsResult => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [activeTab, setActiveTab] = useState<NotificationTab>('all');
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isFetchingRef = useRef(false); // Prevent concurrent fetches
  const avatarCacheRef = useRef<Map<string, string>>(new Map()); // Cache avatar base64 URLs
  const birthdayUserAvatarUrlRef = useRef<Map<number, string | null>>(new Map()); // userId -> full avatar url (or null)
  const birthdayUserFetchInFlightRef = useRef<Set<number>>(new Set()); // prevent duplicate fetches

  // Format thời gian hiển thị: "Bây giờ", "5m", "2h", "3 ngày", hoặc "DD/MM"
  const formatTime = useCallback((dateString?: string): string => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    const diffMs = Date.now() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Bây giờ';
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays} ngày`;
    
    // Hiển thị dạng DD/MM (tận dụng helper chuẩn DD/MM/YYYY)
    const ddmmyyyy = formatDateDisplay(dateString);
    return ddmmyyyy ? ddmmyyyy.slice(0, 5) : '';
  }, []);

  // Kiểm tra xem notification có phải hôm nay không
  const isToday = useCallback((dateString?: string): boolean => {
    if (!dateString) return false;
    const date = new Date(dateString);
    const today = new Date();
    return date.toDateString() === today.toDateString();
  }, []);

  // Fetch danh sách thông báo từ API
  // Có flag để tránh gọi API đồng thời (concurrent fetches)
  const fetchNotifications = useCallback(async () => {
    if (isFetchingRef.current) return;
    
    try {
      isFetchingRef.current = true;
      
      if (isInitialLoad) {
        setLoading(true);
      }
      
      const response = await notificationService.getNotifications({ limit: 100 });

      if (response.success && response.data?.length) {
        // Format thời gian và đánh dấu notification hôm nay
        const formattedNotifications = response.data.map((notif) => ({
          ...notif,
          time: formatTime(notif.createdAt || notif.time),
          isToday: isToday(notif.createdAt),
        }));
        setNotifications(formattedNotifications);
        setError(null); // Clear error on success
      } else {
        setNotifications([]);
        setError(null);
      }
      
      if (isInitialLoad) {
        setIsInitialLoad(false);
      }
    } catch (error: any) {
      // Xử lý lỗi: hiển thị notification và set error state
      handleApiError(error, 'Không thể tải danh sách thông báo', true);
      const errorMessage = error?.message || 'Không thể tải danh sách thông báo';
      setError(errorMessage);
      
      if (isInitialLoad || notifications.length === 0) {
        setNotifications([]);
      }
      
      if (import.meta.env.DEV) {
        console.warn('[Notifications] Lỗi fetch notifications:', error.message);
      }
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, [formatTime, isToday, isInitialLoad, notifications.length]);

  // Fetch notifications khi component mount hoặc dependencies thay đổi
  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Fetch notifications khi refreshTrigger thay đổi
  useEffect(() => {
    if (refreshTrigger && refreshTrigger > 0) {
      fetchNotifications();
    }
  }, [refreshTrigger, fetchNotifications]);

  // Tích hợp socket để nhận thông báo real-time
  const { isConnected, socket } = useNotificationSocket({
    onNewNotification: (newNotification) => {
      // Thêm notification mới vào đầu danh sách nếu chưa có
      setNotifications((prev) => {
        if (prev.some((n) => n.id === newNotification.id)) return prev;
        const formatted = {
          ...newNotification,
          time: formatTime(newNotification.createdAt || newNotification.time),
          isToday: isToday(newNotification.createdAt),
        };
        return [formatted, ...prev];
      });
      onMarkAsRead?.();
    },
    onNotificationRead: (notificationId) => {
      // Cập nhật trạng thái đã đọc khi socket báo
      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, read: true } : n))
      );
    },
    onRefresh: () => fetchNotifications(),
  });

  // Polling khi socket không kết nối: gọi API mỗi 30 giây
  // Chỉ polling khi tab không bị ẩn (document.hidden = false)
  useEffect(() => {
    if ((!socket || !isConnected) && !document.hidden) {
      intervalRef.current = setInterval(() => {
        if (!document.hidden && !isInitialLoad) {
          fetchNotifications();
        }
      }, 30000);
      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      };
    } else if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, [socket, isConnected, fetchNotifications, isInitialLoad]);

  // Fetch notifications khi tab được focus hoặc visible
  // Debounce 1 giây để tránh gọi quá nhiều
  useEffect(() => {
    let focusTimeout: ReturnType<typeof setTimeout> | null = null;
    let visibilityTimeout: ReturnType<typeof setTimeout> | null = null;

    const debouncedFetch = () => {
      if (!document.hidden && !isInitialLoad) {
        fetchNotifications();
      }
    };

    const handleFocus = () => {
      if (focusTimeout) clearTimeout(focusTimeout);
      focusTimeout = setTimeout(debouncedFetch, 1000);
    };

    const handleVisibilityChange = () => {
      if (visibilityTimeout) clearTimeout(visibilityTimeout);
      if (!document.hidden && !isInitialLoad) {
        visibilityTimeout = setTimeout(debouncedFetch, 1000);
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (focusTimeout) clearTimeout(focusTimeout);
      if (visibilityTimeout) clearTimeout(visibilityTimeout);
    };
  }, [fetchNotifications, isInitialLoad]);

  // Helper để fetch avatar và cache
  const fetchAvatarAsDataUri = useCallback(async (imageUrl: string): Promise<string | null> => {
    if (avatarCacheRef.current.has(imageUrl)) {
      return avatarCacheRef.current.get(imageUrl) || null;
    }

    const dataUri = await fetchImageAsDataUri(imageUrl);
    if (dataUri) {
      avatarCacheRef.current.set(imageUrl, dataUri);
    }
    return dataUri;
  }, []);

  // Xác định loại notification: Update request (yêu cầu cập nhật thông tin)
  const isUpdateRequestNotification = useCallback((item: Notification): boolean => {
    return (
      item.relatedResourceType === 'user-update-request' ||
      item.title?.includes('Yêu cầu cập nhật') ||
      item.message?.includes('yêu cầu cập nhật')
    );
  }, []);

  // Xác định loại notification: HR update (HR đã cập nhật thông tin)
  const isHRUpdateNotification = useCallback((item: Notification): boolean => {
    const title = item.title?.toLowerCase().trim() || '';
    const message = item.message?.toLowerCase().trim() || '';
    return (
      title === 'thông tin đã được cập nhật' ||
      title.includes('thông tin đã được cập nhật') ||
      message.includes('hr đã cập nhật') ||
      message.includes('đã cập nhật thông tin của bạn') ||
      message.includes('cập nhật thông tin của bạn')
    );
  }, []);

  // Xác định loại notification: New update request (yêu cầu cập nhật thông tin mới)
  const isNewUpdateRequestNotification = useCallback((item: Notification): boolean => {
    return (
      item.title === 'Yêu cầu cập nhật thông tin mới' ||
      item.title?.includes('Yêu cầu cập nhật thông tin mới') ||
      (item.relatedResourceType === 'user-update-request' && 
       item.message?.includes('đã gửi yêu cầu cập nhật thông tin'))
    );
  }, []);

  // Xử lý click vào notification: đánh dấu đã đọc và điều hướng
  const handleNotificationClick = useCallback(async (item: Notification) => {
    // Đánh dấu đã đọc nếu chưa đọc
    if (!item.read) {
      try {
        setNotifications((prev) => prev.map((n) => (n.id === item.id ? { ...n, read: true } : n)));
        await notificationService.markAsRead(item.id);
        onMarkAsRead?.();
      } catch (error: any) {
        if (import.meta.env.DEV) {
          console.warn('[Notifications] Lỗi mark as read:', error.message);
        }
      }
    }

    // Xử lý navigation cho notification "Yêu cầu cập nhật thông tin"
    if (isUpdateRequestNotification(item)) {
      try {
        let userId: number | undefined;

        // Ưu tiên 1: lấy userId từ notification nếu có (userId của người tạo yêu cầu)
        if (item.userId) {
          userId = item.userId;
        }
        // Ưu tiên 2: Nếu có relatedResourceId, lấy từ update request
        else if (item.relatedResourceId) {
          try {
            const updateRequest = await usersService.getUpdateRequestById(item.relatedResourceId);
            if (updateRequest && updateRequest.userId) {
              userId = updateRequest.userId;
            }
          } catch (error: any) {
            console.error('Error fetching update request:', error);
          }
        }
        // Ưu tiên 3: Thử lấy từ data nếu có
        else if (item.data?.userId) {
          userId = item.data.userId;
        }

        if (userId) {
          // Nếu user hiện tại là STAFF, điều hướng đến profile cá nhân
          // Nếu là Admin/CEO/HR, điều hướng đến trang quản lý nhân sự
          // Thêm timestamp để đảm bảo state luôn khác nhau và trigger re-render
          const refreshState = { refresh: true, timestamp: Date.now() };
          
          if (user?.role === 'staff') {
            navigate('/personal/profile', { 
              state: refreshState
            });
          } else {
            // Fetch user để lấy employeeCode
            try {
              const userDetail = await usersService.getUserById(userId);
              const employeeCode = userDetail.employeeCode || `NV${userId}`;
              navigate(`/management/personnel/detail/${employeeCode}`, { 
                state: refreshState
              });
            } catch (error) {
              console.error('Error fetching user detail:', error);
              // Fallback: dùng userId nếu không fetch được employeeCode
              navigate(`/management/personnel/detail/NV${userId}`, { 
                state: refreshState
              });
            }
          }
        } else {
          console.warn('Could not determine userId for notification:', item);
        }
      } catch (error: any) {
        console.error('Error handling notification click:', error);
      }
    }
  }, [navigate, onMarkAsRead, user, isUpdateRequestNotification]);

  // Đánh dấu tất cả thông báo đã đọc
  const markAllAsRead = useCallback(async () => {
    try {
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      await notificationService.markAllAsRead();
      onMarkAsRead?.();
    } catch (error: any) {
      if (import.meta.env.DEV) {
        console.warn('[Notifications] Lỗi mark all as read:', error.message);
      }
    }
  }, [onMarkAsRead]);

  // Xóa nhiều notifications
  const deleteNotifications = useCallback(async (ids: string[]) => {
    if (!ids || ids.length === 0) return;

    const prev = notifications;
    try {
      // Optimistic update
      setNotifications((cur) => cur.filter((n) => !ids.includes(String(n.id))));
      await notificationService.deleteMany(ids);
      onMarkAsRead?.(); // trigger refresh badges in header if any
    } catch (error: any) {
      // Revert + refetch for safety
      setNotifications(prev);
      handleApiError(error, 'Không thể xóa thông báo', true);
      await fetchNotifications();
    }
  }, [notifications, onMarkAsRead, fetchNotifications]);

  // Refresh danh sách thông báo
  const handleRefresh = useCallback(async () => {
    await fetchNotifications();
  }, [fetchNotifications]);

  // Filter notifications theo tab hiện tại
  const filteredNotifications = useMemo(() => {
    switch (activeTab) {
      case 'unread':
        return notifications.filter((n) => !n.read);
      case 'today':
        return notifications.filter((n) => n.isToday);
      default:
        return notifications;
    }
  }, [activeTab, notifications]);

  // Đếm số thông báo chưa đọc
  const unreadCount = useMemo(() => notifications.filter((n) => !n.read).length, [notifications]);
  
  // Đếm số thông báo hôm nay chưa đọc
  const todayUnreadCount = useMemo(() => notifications.filter((n) => n.isToday && !n.read).length, [notifications]);

  // Xác định loại notification: Birthday (sinh nhật)
  const isBirthdayNotification = useCallback((item: Notification) => {
    return (
      item.title === 'Sinh nhật hôm nay' ||
      (item.relatedResourceType === 'user' &&
        item.type === 'success' &&
        (item.message?.toLowerCase().includes('sinh nhật') || item.message?.includes('🎉')))
    );
  }, []);

  // Lấy tên người sinh nhật từ notification
  const getBirthdayPersonName = useCallback((item: Notification): string | null => {
    // Prefer metadata nếu backend cung cấp trong item.data
    const fromData = item.data?.birthdayPersonName as string | undefined;
    if (fromData && fromData.trim()) return fromData.trim();

    // Fallback: parse message "Hôm nay là sinh nhật của <NAME>."
    const msg = item.message || '';
    const m = msg.match(/sinh nhật của\s+(.+?)(?:\.|!|\n|$)/i);
    if (m?.[1]) return m[1].trim();
    return null;
  }, []);

  // Lấy ID người sinh nhật từ notification
  const getBirthdayPersonId = useCallback((item: Notification): number | null => {
    // Prefer metadata
    const fromData = item.data?.birthdayPersonId as number | string | undefined;
    if (typeof fromData === 'number') return fromData;
    if (typeof fromData === 'string' && fromData.trim() && !Number.isNaN(Number(fromData))) {
      return Number(fromData);
    }
    // Fallback: relatedResourceId (birthday notification sets relatedResourceType='user')
    if (typeof item.relatedResourceId === 'number') return item.relatedResourceId;
    return null;
  }, []);

  // Lấy avatar người sinh nhật từ notification
  const getBirthdayPersonAvatar = useCallback((item: Notification): string | null => {
    // Prefer metadata từ backend (avatar của nhân sự sinh nhật)
    const metaAvatar = item.data?.birthdayPersonAvatar as string | undefined;
    if (metaAvatar && metaAvatar.trim()) return buildFullUrl(metaAvatar);

    // Prefer cached lookup by userId (giống cách danh sách nhân sự fetch detail)
    const personId = getBirthdayPersonId(item);
    if (personId && birthdayUserAvatarUrlRef.current.has(personId)) {
      return birthdayUserAvatarUrlRef.current.get(personId) || null;
    }

    // Fallback: dùng avatar đi kèm notification nếu có
    if (item.avatar?.trim()) return buildFullUrl(item.avatar);

    return null;
  }, [getBirthdayPersonId]);

  // Lấy thông tin người duyệt (approver) từ notification
  const getApproverInfo = useCallback((item: Notification): { id: number; fullName: string; avatar?: string } | null => {
    const approver = item.data?.approver as { id: number; fullName: string; avatar?: string } | undefined;
    return approver || null;
  }, []);

  // Lấy thông tin người từ chối (rejector) từ notification
  const getRejectorInfo = useCallback((item: Notification): { id: number; fullName: string; avatar?: string } | null => {
    const rejector = item.data?.rejector as { id: number; fullName: string; avatar?: string } | undefined;
    return rejector || null;
  }, []);

  // Lấy thông tin người HR cập nhật (updater) từ notification
  const getHRUpdaterInfo = useCallback((item: Notification): { id: number; fullName: string; avatar?: string } | null => {
    const updater = item.data?.updater as { id: number; fullName: string; avatar?: string } | undefined;
    return updater || null;
  }, []);

  // Lấy thông tin người gửi yêu cầu (requester) từ notification
  const getRequesterInfo = useCallback((item: Notification): { id: number; fullName: string; avatar?: string } | null => {
    const requester = item.data?.requester as { id: number; fullName: string; avatar?: string } | undefined;
    return requester || null;
  }, []);


  // Prefetch avatar cho các notification sinh nhật (giống danh sách nhân sự: gọi usersService.getUserById)
  useEffect(() => {
    const birthdayNotifs = notifications.filter((n) => isBirthdayNotification(n));
    if (birthdayNotifs.length === 0) return;

    for (const n of birthdayNotifs) {
      // Nếu metadata đã có avatar thì khỏi fetch
      const metaAvatar = n.data?.birthdayPersonAvatar as string | undefined;
      if (metaAvatar && metaAvatar.trim()) continue;

      const personId = getBirthdayPersonId(n);
      if (!personId) continue;

      // Đã có cache hoặc đang fetch thì bỏ qua
      if (birthdayUserAvatarUrlRef.current.has(personId)) continue;
      if (birthdayUserFetchInFlightRef.current.has(personId)) continue;

      birthdayUserFetchInFlightRef.current.add(personId);

      usersService
        .getUserById(personId)
        .then((userDetail: any) => {
          const avatarPath = userDetail?.personalAvatar || userDetail?.avatar || null;
          const full = avatarPath ? buildFullUrl(avatarPath) : null;
          birthdayUserAvatarUrlRef.current.set(personId, full);
        })
        .catch(() => {
          birthdayUserAvatarUrlRef.current.set(personId, null);
        })
        .finally(() => {
          birthdayUserFetchInFlightRef.current.delete(personId);
          // Trigger re-render để Avatar cập nhật
          setNotifications((prev) => [...prev]);
        });
    }
  }, [notifications, isBirthdayNotification, getBirthdayPersonId]);

  return {
    // Data
    notifications,
    loading,
    error,
    isInitialLoad,

    // Tabs
    activeTab,
    setActiveTab,
    filteredNotifications,

    // Counts
    unreadCount,
    todayUnreadCount,

    // Actions
    handleNotificationClick,
    markAllAsRead,
    handleRefresh,
    deleteNotifications,

    // Avatar helpers
    fetchAvatarAsDataUri,
    avatarCacheRef,

    // Notification type helpers
    isBirthdayNotification,
    isUpdateRequestNotification,
    isHRUpdateNotification,
    isNewUpdateRequestNotification,
    getBirthdayPersonName,
    getBirthdayPersonId,
    getBirthdayPersonAvatar,
    getApproverInfo,
    getRejectorInfo,
    getHRUpdaterInfo,
    getRequesterInfo,

    // Socket
    isConnected,
  };
};

